/**
 * Iter 163 · Messaging API client.
 *
 * Backend routes are mounted under /api/messages/*. All calls piggyback on
 * the shared `api` module (cookie-first auth, error envelope, etc.).
 */
import { api } from '@/lib/api';

export const messagingApi = {
  // ---- Conversations
  openConversation(targetUsername) {
    return api.post('/messages/conversations', { target_username: targetUsername });
  },
  listConversations({ limit = 25, offset = 0 } = {}) {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return api.get(`/messages/conversations?${qs.toString()}`);
  },
  getConversation(conversationId) {
    return api.get(`/messages/conversations/${conversationId}`);
  },

  // ---- Messages
  listMessages(conversationId, { limit = 50, before } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (before) qs.set('before', before);
    return api.get(`/messages/conversations/${conversationId}/messages?${qs.toString()}`);
  },
  /**
   * Send a message.
   *
   * Backward-compatible signature — the composer just passes a plain
   * string. Iter 163 · Extensibility: pass `{ messageType, payload }`
   * for future audio/sticker/system markers. The backend rejects
   * anything other than `text` today with a 400
   * `message_type_not_supported` — this is intentional, not a bug.
   */
  sendMessage(conversationId, text, opts = {}) {
    const body = { text };
    if (opts.messageType && opts.messageType !== 'text') {
      body.message_type = opts.messageType;
    }
    if (opts.payload) {
      body.payload = opts.payload;
    }
    return api.post(`/messages/conversations/${conversationId}/messages`, body);
  },
  markRead(conversationId) {
    return api.post(`/messages/conversations/${conversationId}/read`, {});
  },
  unreadCount() {
    return api.get('/messages/unread-count');
  },

  // ---- Blocks
  block(username) {
    return api.post(`/messages/blocks/${encodeURIComponent(username)}`, {});
  },
  unblock(username) {
    return api.delete(`/messages/blocks/${encodeURIComponent(username)}`);
  },
  listBlocks() {
    return api.get('/messages/blocks');
  },

  // ---- Reports
  report({ reason, messageId, conversationId, details }) {
    const body = { reason };
    if (messageId) body.message_id = messageId;
    if (conversationId) body.conversation_id = conversationId;
    if (details) body.details = details;
    return api.post('/messages/reports', body);
  },

  // ---- Iter 174 · Read receipts
  /** Receiver marks an audio message as listened (idempotent). */
  markListened(messageId) {
    return api.post(`/messages/listened/${encodeURIComponent(messageId)}`);
  },

  // ---- Iter 164 · Reactions
  // One reaction per user per message. Passing a new emoji upserts and
  // implicitly replaces the previous one. `removeReaction` is idempotent.
  setReaction(messageId, emoji) {
    return api.put(`/messages/reactions/${encodeURIComponent(messageId)}`, { emoji });
  },
  removeReaction(messageId) {
    return api.delete(`/messages/reactions/${encodeURIComponent(messageId)}`);
  },
};

export default messagingApi;
